import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';

const PLACEHOLDERS = [
  '{{PROPERTY_ADDRESS}}',
  '{{FILE_NAME}}',
  '{{CLOSING_DATE}}',
  '{{ETA_DATE}}',
  '{{PREVIOUS_ETA}}',
  '{{NEW_ETA}}',
  '{{ETA_REASON}}',
  '{{PROCESSOR_NAME}}'
];

const draftFrom = template => ({ taskTitle: template?.taskTitle || '', body: template?.body || '' });
const panelInsetStyle = { paddingLeft: 24, paddingRight: 24 };
const templateCardStyle = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 };

const QualiaMessageTemplatesPanel = ({ setActiveTabItemsMetadata }) => {
  const [templates, setTemplates] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setActiveTabItemsMetadata?.({ count: 0, loading: true });
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.QUALIA_MESSAGE_TEMPLATES,
      method: 'GET'
    });
    if (error) {
      toast.error(error);
      setActiveTabItemsMetadata?.({ count: 0, loading: false });
      setLoading(false);
      return;
    }
    const list = Array.isArray(response) ? response : [];
    setTemplates(list);
    setDrafts(list.reduce((acc, template) => ({ ...acc, [template.key]: draftFrom(template) }), {}));
    setActiveTabItemsMetadata?.({ count: list.length, loading: false });
    setLoading(false);
  }, [setActiveTabItemsMetadata]);

  useEffect(() => {
    load();
  }, [load]);

  const ordered = useMemo(() => {
    const order = ['info_requested', 'eta_change'];
    return [...templates].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }, [templates]);

  const updateField = (key, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [key]: { ...(prev[key] || draftFrom()), [field]: value }
    }));
  };

  /** Insert at cursor in the focused Task title / Message field (mousedown preventDefault keeps focus). */
  const insertPlaceholder = token => {
    const el = document.activeElement;
    const key = el?.dataset?.templateKey;
    const field = el?.dataset?.field;
    if (!key || !field || typeof el.selectionStart !== 'number') return;

    const value = drafts[key]?.[field] || '';
    const { selectionStart: start, selectionEnd: end } = el;
    const next = value.slice(0, start) + token + value.slice(end);
    updateField(key, field, next);
    const pos = start + token.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const save = async key => {
    const draft = drafts[key] || draftFrom();
    if (!draft.taskTitle?.trim()) {
      toast.error('Task title cannot be empty');
      return;
    }
    if (!draft.body?.trim()) {
      toast.error('Template body cannot be empty');
      return;
    }
    setSavingKey(key);
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.QUALIA_MESSAGE_TEMPLATE_WITH_KEY(key),
      method: 'PUT',
      requestBody: { taskTitle: draft.taskTitle, body: draft.body }
    });
    setSavingKey(null);
    if (error) {
      toast.error(error);
      return;
    }
    setTemplates(prev => prev.map(t => (t.key === key ? response : t)));
    setDrafts(prev => ({
      ...prev,
      [key]: draftFrom(response)
    }));
    toast.success('Template saved');
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spinner animation="border" size="sm" className="mr-2" />
        <span className="text-muted">Loading Qualia message templates…</span>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 px-md-3">
      <div style={panelInsetStyle}>
        <p className="text-muted midFont mb-3">
          Filled in when a Qualia client order moves to Information Requested or an existing ETA changes.
          Edits only affect tasks created afterwards. Click a placeholder to insert it at the cursor.
        </p>
        <div className="mb-4">
          <p className="text-muted tinyFont mb-1">Placeholders:</p>
          <div className="mb-1">
            {PLACEHOLDERS.map(token => (
              <button
                key={token}
                type="button"
                className="btn btn-sm btn-light border mr-1 mb-1"
                style={{ fontFamily: 'monospace', fontSize: 11 }}
                onMouseDown={e => e.preventDefault()}
                onClick={() => insertPlaceholder(token)}
              >
                {token}
              </button>
            ))}
          </div>
          <p className="text-muted tinyFont mb-0">
            If ETA reason is blank, the “Reason for Change” line is removed from the message.
          </p>
        </div>
        <Row>
          {ordered.map(template => {
            const draft = drafts[template.key] || draftFrom();
            const saved = draftFrom(template);
            const dirty = draft.taskTitle !== saved.taskTitle || draft.body !== saved.body;
            const isSaving = savingKey === template.key;
            return (
              <Col key={template.key} xs={12} lg={6} className="mb-4">
                <div className="h-100 p-3" style={templateCardStyle}>
                  <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                    <h6 className="font-weight-bold mb-0 min-w-0">{template.label}</h6>
                    <Button
                      variant="info"
                      size="sm"
                      className="d-flex align-items-center flex-shrink-0"
                      style={{ gap: 6 }}
                      disabled={!dirty || isSaving}
                      onClick={() => save(template.key)}
                    >
                      {isSaving ? <Spinner animation="border" size="sm" /> : <Save size={14} />}
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                  <Form.Group className="mb-2">
                    <Form.Label className="tinyFont text-muted mb-1">Task title</Form.Label>
                    <Form.Control
                      type="text"
                      data-template-key={template.key}
                      data-field="taskTitle"
                      value={draft.taskTitle}
                      onChange={e => updateField(template.key, 'taskTitle', e.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="tinyFont text-muted mb-1">Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={10}
                      data-template-key={template.key}
                      data-field="body"
                      value={draft.body}
                      onChange={e => updateField(template.key, 'body', e.target.value)}
                      style={{ fontFamily: 'monospace', fontSize: 13 }}
                    />
                  </Form.Group>
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
    </div>
  );
};

export default QualiaMessageTemplatesPanel;
